'use client'

import { useEffect, useRef } from 'react'

export function BlogReadingProgress() {
  const barRef = useRef<HTMLDivElement>(null)
  const fillRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Every brand nav (NavBarMain/Flow/Comet/Galaxy/Gravity) renders as a
    // sticky <header> at the top of the page, but none render at a fixed
    // height — it's intrinsic/content-based padding, and varies slightly
    // by brand (e.g. 68px here vs. the 64px this bar used to hardcode).
    // A hardcoded `top` therefore silently sits a few px short on some
    // brands, landing entirely underneath the header's opaque, higher
    // z-index background — invisible, even though the fill is updating
    // correctly underneath. Measure the real header height instead.
    const positionBar = () => {
      const header = document.querySelector('header')
      if (barRef.current) barRef.current.style.top = `${header?.getBoundingClientRect().height ?? 64}px`
    }
    const update = () => {
      const scrollTop = window.scrollY
      // Track progress through the article body itself, not the whole page —
      // otherwise the bar moves before the article starts (hero/header) and
      // never reaches 100% at the end (newsletter + footer follow the article).
      const article = document.getElementById('blog-article-body')
      let progress: number
      if (article) {
        const rect = article.getBoundingClientRect()
        const articleTop = rect.top + scrollTop
        const denom = Math.max(rect.height - window.innerHeight, 1)
        progress = Math.min(1, Math.max(0, (scrollTop - articleTop) / denom))
      } else {
        const max = document.documentElement.scrollHeight - window.innerHeight
        progress = max > 0 ? Math.min(1, Math.max(0, scrollTop / max)) : 0
      }
      if (fillRef.current) fillRef.current.style.transform = `scaleX(${progress})`
    }
    positionBar()
    update()
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    window.addEventListener('resize', positionBar)
    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      window.removeEventListener('resize', positionBar)
    }
  }, [])

  return (
    <div ref={barRef} className="blog-progress" aria-hidden="true">
      <div ref={fillRef} className="blog-progress__fill" style={{ transform: 'scaleX(0)' }} />
    </div>
  )
}
